import { useEffect, useMemo, useState } from 'react';
import { Canvas } from '@react-three/fiber';
import { ChessAdapter } from '@strategy4kids/game-engine';
import {
  LessonBoard,
  lessonBoardProps,
  pieceThemeById,
  boardThemeById,
  tableThemeByMaterial,
  pieceMaterialById,
  pieceSizeById,
} from '@strategy4kids/board-3d';
import { ALL_LESSONS, gradeDrill, type Grade } from '@strategy4kids/lessons';
import type { Lesson } from '@strategy4kids/lessons';
import { Button, Celebration, Icon } from '@strategy4kids/ui';
import { SquareOverlay } from '../components/SquareOverlay.js';
import { useBoardReady } from '../components/useBoardReady.js';
import { SpeakerButton } from '../components/SpeakerButton.js';
import { api } from '../services/api.js';
import { prefetchTts, speak } from '../services/speech.js';
import { useProfileStore } from '../state/profile-store.js';
import { useProgressStore } from '../state/progress-store.js';
import { effectiveAudio, useAudioSettingsStore } from '../state/audio-settings-store.js';
import { useThemeStore } from '../state/theme-store.js';
import { hintForAttempts } from '../lib/drill-hints.js';
import { useT } from '../i18n/useT.js';
import { PANEL_CLASS } from '../components/PageHeader.js';
import { ScreenLayout } from '../components/ScreenLayout.js';

const adapter = new ChessAdapter();

/** Lesson completion grade from the number of wrong attempts in the lesson. */
export function gradeForAttempts(mistakes: number): Grade {
  if (mistakes === 0) return 'gold';
  if (mistakes <= 2) return 'silver';
  return 'bronze';
}

/** The text to speak for a lesson step, or null when there is nothing to say. */
export function stepSpeakText(step: Lesson['steps'][number]): string | null {
  if (step.kind === 'explain') return step.narration ?? step.body;
  if (step.kind === 'demo') return step.body;
  return null;
}

export interface LessonScreenProps {
  readonly lessonId: string;
  readonly onExit: () => void;
  /** Test seam — defaults to the full authored catalog. */
  readonly lessons?: readonly Lesson[];
}

type Target = { square: string; capture: boolean };

export function LessonScreen({ lessonId, onExit, lessons = ALL_LESSONS }: LessonScreenProps) {
  const { t } = useT('lessons');
  const lesson = useMemo(() => lessons.find((l) => l.id === lessonId), [lessons, lessonId]);
  const [stepIndex, setStepIndex] = useState(0);
  const [mistakes, setMistakes] = useState(0);
  const [drillSelected, setDrillSelected] = useState<string | null>(null);
  const [drillTargets, setDrillTargets] = useState<readonly Target[]>([]);
  const [drillFeedback, setDrillFeedback] = useState<string | null>(null);
  const [drillOverlayTransform, setDrillOverlayTransform] = useState<string>();
  const [hintIndex, setHintIndex] = useState(0);
  const [quizIndex, setQuizIndex] = useState(0);
  const [quizChoice, setQuizChoice] = useState<number | null>(null);
  const [celebrate, setCelebrate] = useState(false);
  // Only the drill board carries a SquareOverlay; the demo board above it is
  // display-only, so it needs no readiness gate.
  const [drillBoardReady, markDrillBoardReady] = useBoardReady();

  const step = lesson ? lesson.steps[stepIndex] : undefined;

  const token = useProfileStore((s) => s.profile?.token);
  // The EFFECTIVE voice settings: a grown-up's "voices off" lock silences the lesson even though
  // the switch below is still on screen.
  const voiceOn = useAudioSettingsStore((s) => effectiveAudio(s).voiceOn);
  const autoSpeak = useAudioSettingsStore((s) => effectiveAudio(s).narratorOn);
  const voicesLocked = useAudioSettingsStore((s) => s.locks.voices);
  const setVoiceOn = useAudioSettingsStore((s) => s.setVoiceOn);
  const setNarratorOn = useAudioSettingsStore((s) => s.setNarratorOn);
  const pieceThemeId = useThemeStore((s) => s.pieceThemeId);
  const boardThemeId = useThemeStore((s) => s.boardThemeId);
  const tableMaterialId = useThemeStore((s) => s.tableMaterialId);
  const tableToneId = useThemeStore((s) => s.tableToneId);
  const pieceMaterialId = useThemeStore((s) => s.pieceMaterialId);
  const pieceSizeId = useThemeStore((s) => s.pieceSizeId);
  const pieceWhiteColor = useThemeStore((s) => s.pieceWhiteColor);
  const pieceBlackColor = useThemeStore((s) => s.pieceBlackColor);
  const showCoords = useThemeStore((s) => s.showCoords);

  const speakText = step ? stepSpeakText(step) : null;

  useEffect(() => {
    if (!token || !voiceOn || !speakText) return;
    if (autoSpeak) void speak(speakText, { token });
    else prefetchTts(speakText, { token });
  }, [token, voiceOn, autoSpeak, speakText]);

  const pos = useMemo(
    () => (step && (step.kind === 'demo' || step.kind === 'drill') ? adapter.fromFen(step.fen) : null),
    [step],
  );
  // Free drills (the board-control lesson) apply legal moves live so the control heat
  // map recomputes. liveFen holds the applied position; null means "back at step.fen".
  const [liveFen, setLiveFen] = useState<string | null>(null);
  const currentPos = step?.kind === 'drill' && step.free && liveFen ? adapter.fromFen(liveFen) : pos;

  if (!lesson || !step) {
    return (
      <ScreenLayout width="wide" center>
        <p className="text-lg text-ink/70">{t('lesson-not-found')}</p>
        <Button variant="ghost" onClick={onExit}>
          {t('back-to-learn')}
        </Button>
      </ScreenLayout>
    );
  }

  const question = step.kind === 'quiz' ? step.questions[quizIndex] : undefined;
  const drillHintText = step.kind === 'drill' ? hintForAttempts(step, hintIndex) : null;

  /** Advance to the next step, or finish the lesson with a grade at the last step. */
  const advance = (nextMistakes: number) => {
    if (stepIndex === lesson.steps.length - 1) {
      const grade = gradeForAttempts(nextMistakes);
      useProgressStore.getState().recordResult(lesson.id, grade);
      const token = useProfileStore.getState().profile?.token;
      if (token) {
        void api.completeLesson(token, lesson.id, grade).catch(() => {
          // offline/edge: local progress still recorded; retried on next finish
        });
      }
      setCelebrate(true);
    } else {
      setStepIndex(stepIndex + 1);
      setMistakes(nextMistakes);
    }
    setDrillSelected(null);
    setDrillTargets([]);
    setDrillFeedback(null);
    setHintIndex(0);
    setLiveFen(null);
    setQuizIndex(0);
    setQuizChoice(null);
  };

  const onQuizCheck = () => {
    if (quizChoice === null || !question || step.kind !== 'quiz') return;
    if (quizChoice === question.correct) {
      if (quizIndex === step.questions.length - 1) {
        advance(mistakes);
      } else {
        setQuizIndex(quizIndex + 1);
        setQuizChoice(null);
      }
    } else {
      setMistakes((m) => m + 1);
      setQuizChoice(null);
    }
  };

  const onDrillTap = (square: string) => {
    if (!currentPos || step.kind !== 'drill') return;
    if (drillSelected === null) {
      const piece = adapter.pieceAt(currentPos, square);
      if (piece && piece.color === currentPos.turn) {
        setDrillSelected(square);
        setDrillTargets(
          adapter.legalMovesForSquare(currentPos, square).map((t) => ({ square: t.to, capture: t.capture })),
        );
      }
      return;
    }
    if (square === drillSelected) {
      setDrillSelected(null);
      setDrillTargets([]);
      return;
    }
    // Tapping a different own piece re-selects it rather than grading a move —
    // otherwise a kid re-selecting a piece would burn a mistake on an illegal move.
    const tapped = adapter.pieceAt(currentPos, square);
    if (tapped && tapped.color === currentPos.turn) {
      setDrillSelected(square);
      setDrillTargets(
        adapter.legalMovesForSquare(currentPos, square).map((t) => ({ square: t.to, capture: t.capture })),
      );
      return;
    }
    const result = gradeDrill({ adapter, pos: currentPos, step, turn: currentPos.turn }, drillSelected, square);
    if (!result.pass) {
      setMistakes((m) => m + 1);
      setDrillFeedback(result.feedback);
      setHintIndex((i) => i + 1);
      setDrillSelected(null);
      setDrillTargets([]);
      return;
    }
    if (step.free) {
      // Live board-control drill: apply the move, keep drilling, recompute the heat map.
      setLiveFen(adapter.makeMove(currentPos, drillSelected, square).fen);
      setDrillSelected(null);
      setDrillTargets([]);
      return;
    }
    advance(mistakes);
  };

  return (
    <ScreenLayout width="wide" data-testid="lesson-screen" data-appdom-scope="lesson">
      {/*
        * Grid rather than two stacked flex rows, so the step counter is ONE
        * element at every width.
        *
        * It used to be two spans carrying the same `data-testid`, one
        * `sm:hidden` and one `hidden sm:inline`, because the narrow layout puts
        * it beside the title and the wide layout puts it beside the voice
        * toggles. Exactly one was ever displayed, so it looked harmless, but a
        * locator matching a testid twice resolves to `.first()` — the narrow
        * one — which is `display: none` at >= 640px. Every audit flow asserting
        * the counter above that width was therefore reading a hidden element:
        * `lesson` had been reporting WARNING in gating shard 1 for exactly this
        * and passing anyway, and `responsive` asserts the counter is visible at
        * all four viewports, which two slots could never satisfy.
        *
        * Layout is unchanged. Narrow: title and counter on row 1, voice
        * settings right-aligned on row 2. Wide: all three on one row with the
        * voice settings at the end.
        */}
      <header className={`${PANEL_CLASS} grid grid-cols-[1fr_auto] items-center gap-3 px-5 py-4 sm:grid-cols-[1fr_auto_auto] sm:px-8 sm:py-5 lg:px-10`}>
        <h1 className="col-start-1 row-start-1 text-3xl font-black text-berry lg:text-4xl">{lesson.title}</h1>
        <span
          className="col-start-2 row-start-1 justify-self-end text-base font-bold text-ink/70"
          data-testid="lesson-step-counter"
        >
          {t('step-counter', { current: stepIndex + 1, total: lesson.steps.length })}
        </span>
        <div className="col-span-2 row-start-2 flex items-center justify-end gap-3 sm:col-span-1 sm:col-start-3 sm:row-start-1">
          <div
            className="flex items-center gap-2"
            data-testid="voice-settings"
            data-appdom-id="voice-settings"
            data-appdom-type="Toolbar"
          >
            <button
              type="button"
              data-testid="voice-on"
              data-appdom-id="voice-on"
              data-appdom-type="Toggle"
              aria-pressed={voiceOn}
              disabled={voicesLocked}
              onClick={() => setVoiceOn(!voiceOn)}
              className={`min-h-[48px] rounded-xl border-2 px-4 py-2 text-base font-bold ${
                voiceOn ? 'border-berry bg-berry text-white' : 'border-sun/40 bg-cream text-ink/70'
              }`}
            >
              {voiceOn ? t('voice-on') : t('voice-off')}
            </button>
            <button
              type="button"
              data-testid="auto-speak"
              data-appdom-id="auto-speak"
              data-appdom-type="Toggle"
              aria-pressed={autoSpeak}
              disabled={voicesLocked}
              onClick={() => setNarratorOn(!autoSpeak)}
              className={`min-h-[48px] rounded-xl border-2 px-4 py-2 text-base font-bold ${
                autoSpeak ? 'border-berry bg-berry text-white' : 'border-sun/40 bg-cream text-ink/70'
              }`}
            >
              {autoSpeak ? t('auto-speak-on') : t('auto-speak-off')}
            </button>
          </div>
        </div>
      </header>

      {step.kind === 'explain' && (
        <section
          data-testid="lesson-step-explain"
          className={`${PANEL_CLASS} p-5 sm:p-8 lg:p-10`}
          data-appdom-id="lesson-step-explain"
          data-appdom-type="Section"
        >
          <h2 className="text-3xl font-black text-ink lg:text-4xl">{step.title}</h2>
          <p className="mt-3 max-w-3xl text-lg text-ink/80 lg:text-xl">{step.body}</p>
          {step.narration && (
            <p className="mt-3 flex items-start gap-2 font-semibold text-ink/60">
              <Icon name="MessageSquare" size="sm" className="mt-0.5 shrink-0 text-berry" />
              <span>{step.narration}</span>
            </p>
          )}
          {speakText && (
            <div className="mt-3">
              <SpeakerButton text={speakText} />
            </div>
          )}
        </section>
      )}

      {step.kind === 'demo' && (
        <section
          data-testid="lesson-step-demo"
          className={`${PANEL_CLASS} p-5 sm:p-8 lg:p-10`}
          data-appdom-id="lesson-step-demo"
          data-appdom-type="Section"
        >
          <h2 className="text-3xl font-black text-ink lg:text-4xl">{step.title}</h2>
          <p className="mt-3 max-w-3xl text-lg text-ink/80 lg:text-xl">{step.body}</p>
          {speakText && (
            <div className="mt-3">
              <SpeakerButton text={speakText} />
            </div>
          )}
          <div className="mt-4 w-full max-w-xl rounded-3xl bg-board-frame lg:max-w-2xl shadow-xl">
            <Canvas dpr={[1, 2]} camera={{ position: [0, 9, -9], fov: 45 }}>
              <LessonBoard
                view={lessonBoardProps(step)}
                pieceTheme={pieceThemeById(pieceThemeId)}
                boardTheme={boardThemeById(boardThemeId)}
                tableTheme={tableThemeByMaterial(tableMaterialId, tableToneId)}
                pieceMaterial={pieceMaterialById(pieceMaterialId)}
                pieceSize={pieceSizeById(pieceSizeId)}
                pieceWhiteColor={pieceWhiteColor}
                pieceBlackColor={pieceBlackColor}
                showCoords={showCoords}
              />
            </Canvas>
          </div>
        </section>
      )}

      {step.kind === 'drill' && (
        <section
          data-testid="lesson-step-drill"
          className={`${PANEL_CLASS} p-5 sm:p-8 lg:p-10`}
          data-appdom-id="lesson-step-drill"
          data-appdom-type="Section"
        >
          <h2 className="text-3xl font-black text-ink lg:text-4xl">{step.title}</h2>
          <p className="mt-3 max-w-3xl text-lg text-ink/80 lg:text-xl">{step.body}</p>
          <div className="relative mt-4 w-full max-w-xl rounded-3xl bg-board-frame lg:max-w-2xl shadow-xl">
            <Canvas dpr={[1, 2]} camera={{ position: [0, 9, -9], fov: 45 }} onCreated={markDrillBoardReady}>
              <LessonBoard
                view={lessonBoardProps(
                  step.free ? { fen: currentPos?.fen ?? step.fen, free: true } : step,
                  { selected: drillSelected, targets: drillTargets },
                )}
                pieceTheme={pieceThemeById(pieceThemeId)}
                boardTheme={boardThemeById(boardThemeId)}
                tableTheme={tableThemeByMaterial(tableMaterialId, tableToneId)}
                pieceMaterial={pieceMaterialById(pieceMaterialId)}
                pieceSize={pieceSizeById(pieceSizeId)}
                pieceWhiteColor={pieceWhiteColor}
                pieceBlackColor={pieceBlackColor}
                showCoords={showCoords}
                onOverlayTransform={setDrillOverlayTransform}
              />
            </Canvas>
            <SquareOverlay
              selected={drillSelected}
              targets={drillTargets}
              onTap={onDrillTap}
              ready={drillBoardReady}
              transform={drillOverlayTransform}
            />
          </div>
          {drillHintText && drillSelected === null && (
            <p
              className="mt-2 flex items-start gap-2 text-sm font-semibold text-ink/60"
              data-testid="drill-hint"
            >
              <Icon name="Lightbulb" size="sm" className="mt-0.5 shrink-0 text-sun" />
              <span>{drillHintText}</span>
            </p>
          )}
          {drillFeedback && (
            <p className="mt-2 font-bold text-berry" data-testid="drill-feedback">
              {drillFeedback}
            </p>
          )}
        </section>
      )}

      {step.kind === 'quiz' && question && (
        <section
          data-testid="lesson-step-quiz"
          className={`${PANEL_CLASS} p-5 sm:p-8 lg:p-10`}
          data-appdom-id="lesson-step-quiz"
          data-appdom-type="Section"
        >
          <h2 className="text-3xl font-black text-ink lg:text-4xl">{step.title}</h2>
          <p className="mt-3 max-w-3xl text-lg text-ink/80 lg:text-xl">{step.body}</p>
          <div className="mt-4 rounded-3xl border-2 border-sun/40 bg-cream p-5">
            <p className="text-xl font-extrabold text-ink">{question.prompt}</p>
            <div className="mt-3 flex flex-col gap-2">
              {question.choices.map((choice, i) => (
                <button
                  key={choice}
                  type="button"
                  data-testid={`quiz-choice-${i}`}
                  data-appdom-id={`quiz-choice-${i}`}
                  data-appdom-type="Option"
                  onClick={() => setQuizChoice(i)}
                  className={`rounded-2xl border-2 px-4 py-3 text-left font-bold transition-colors ${
                    quizChoice === i ? 'border-berry bg-berry/10 text-ink' : 'border-sun/40 bg-white text-ink/70'
                  }`}
                >
                  {choice}
                </button>
              ))}
            </div>
            <Button
              data-testid="quiz-check"
              data-appdom-id="quiz-check"
              data-appdom-type="Button"
              className="mt-4"
              disabled={quizChoice === null}
              onClick={onQuizCheck}
            >
              {t('check')}
            </Button>
          </div>
        </section>
      )}

      <div className={`${PANEL_CLASS} mt-auto flex items-center justify-between gap-3 px-4 py-3 sm:px-6`}>
        <Button
          variant="secondary"
          data-testid="lesson-back"
          data-appdom-id="lesson-back"
          data-appdom-type="Button"
          onClick={onExit}
        >
          {t('back-to-learn')}
        </Button>
        {(step.kind === 'explain' || step.kind === 'demo') && (
          <Button
            data-testid="lesson-next"
            data-appdom-id="lesson-next"
            data-appdom-type="Button"
            onClick={() => advance(mistakes)}
          >
            {t('next')}
          </Button>
        )}
        {step.kind === 'drill' && step.free && liveFen !== null && (
          <Button
            data-testid="drill-done"
            data-appdom-id="drill-done"
            data-appdom-type="Button"
            onClick={() => advance(mistakes)}
          >
            {t('done')}
          </Button>
        )}
      </div>

      {celebrate && <Celebration grade={gradeForAttempts(mistakes)} title={lesson.title} onNext={onExit} />}
    </ScreenLayout>
  );
}
